<template>
    <div v-if="analytics.available" class="aa">
        <!-- ---------- Efficiency + where the points came from ---------- -->
        <div class="aa-section-title">
            <span class="aa-section-title__bar custom-orange" />
            Αποδοτικότητα
            <info-tip text="Εκτίμηση: σουτ − επιθ. ριμπάουντ + λάθη + 0,44 × βολές, μέσος όρος των δύο ομάδων">
                <span class="aa-chip">
                    <v-icon x-small color="orange darken-2">mdi-swap-horizontal</v-icon>
                    {{ fmt(analytics.efficiency.possessions, 0) }} κατοχές
                </span>
            </info-tip>
            <info-tip v-if="analytics.efficiency.pace !== null" text="Κατοχές ανά 40 λεπτά">
                <span class="aa-chip">
                    <v-icon x-small color="orange darken-2">mdi-speedometer</v-icon>
                    Ρυθμός {{ fmt(analytics.efficiency.pace, 1) }}
                </span>
            </info-tip>
        </div>
        <div class="aa-two-cols">
            <div class="aa-card">
                <div class="aa-card__subtitle">Four factors & ratings</div>
                <div class="aa-teams">
                    <span v-for="side in teams" :key="side.key" class="aa-team" :class="{ 'aa-team--right': side.key === 'team2' }">
                        <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                        <span>{{ side.team.name }}</span>
                    </span>
                </div>
                <div v-for="row in efficiencyRows" :key="row.key" class="aa-row">
                    <div class="aa-value" :class="{ 'aa-value--lead': row.lead === 1 }">{{ row.display1 }}</div>
                    <div class="aa-middle">
                        <span class="aa-label">
                            {{ row.label }}
                            <info-tip :text="row.info"><v-icon x-small class="aa-info">mdi-information-outline</v-icon></info-tip>
                        </span>
                        <div class="aa-bars">
                            <div class="aa-bar aa-bar--left"><span :style="{ width: `${row.share1}%`, background: color1 }" /></div>
                            <div class="aa-bar"><span :style="{ width: `${row.share2}%`, background: color2 }" /></div>
                        </div>
                    </div>
                    <div class="aa-value aa-value--right" :class="{ 'aa-value--lead': row.lead === 2 }">{{ row.display2 }}</div>
                </div>
            </div>

            <div class="aa-card">
                <div class="aa-card__subtitle">Από πού ήρθαν οι πόντοι</div>
                <div class="aa-teams">
                    <span v-for="side in teams" :key="side.key" class="aa-team" :class="{ 'aa-team--right': side.key === 'team2' }">
                        <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                        <span>{{ side.team.name }}</span>
                    </span>
                </div>
                <div v-for="row in sourceRows" :key="row.key" class="aa-row">
                    <div class="aa-value" :class="{ 'aa-value--lead': row.lead === 1 }">
                        {{ row.display1 }}<small>{{ row.detail1 }}</small>
                    </div>
                    <div class="aa-middle">
                        <span class="aa-label">
                            {{ row.label }}
                            <info-tip :text="row.info"><v-icon x-small class="aa-info">mdi-information-outline</v-icon></info-tip>
                        </span>
                        <div class="aa-bars">
                            <div class="aa-bar aa-bar--left"><span :style="{ width: `${row.share1}%`, background: color1 }" /></div>
                            <div class="aa-bar"><span :style="{ width: `${row.share2}%`, background: color2 }" /></div>
                        </div>
                    </div>
                    <div class="aa-value aa-value--right" :class="{ 'aa-value--lead': row.lead === 2 }">
                        {{ row.display2 }}<small>{{ row.detail2 }}</small>
                    </div>
                </div>
                <div class="aa-note">Οι κατηγορίες επικαλύπτονται (π.χ. ένα καλάθι μπορεί να είναι και από ασίστ και 2ης ευκαιρίας). Ποσοστό επί των πόντων της ομάδας.</div>
            </div>
        </div>

        <!-- ---------- Shot profile ---------- -->
        <template v-if="analytics.profile">
            <div class="aa-section-title">
                <span class="aa-section-title__bar custom-orange" />
                Προφίλ σουτ
            </div>
            <div class="aa-two-cols">
                <div v-for="side in teams" :key="`profile-${side.key}`" class="aa-card">
                    <div class="aa-card__head">
                        <span class="aa-team">
                            <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                            <span>{{ side.team.name }}</span>
                        </span>
                        <span class="aa-card__meta">{{ analytics.profile[side.key].total }} σουτ με θέση</span>
                    </div>
                    <!-- shot diet: where the shots were taken from -->
                    <div class="aa-diet">
                        <span
                            v-for="row in analytics.profile[side.key].rows.filter(item => item.attempts)"
                            :key="row.key"
                            class="aa-diet__part"
                            :class="`aa-diet__part--${row.key}`"
                            :style="{ flexGrow: row.attempts }"
                        >
                            <info-tip :text="`${row.label}: ${fmt(row.share, 0)}% των σουτ`">{{ fmt(row.share, 0) }}%</info-tip>
                        </span>
                    </div>
                    <table class="aa-table">
                        <thead>
                            <tr>
                                <th class="aa-table__first">Ζώνη</th>
                                <th><info-tip text="Εύστοχα / προσπάθειες" underline>Σουτ</info-tip></th>
                                <th>FG%</th>
                                <th><info-tip text="Πόντοι ανά σουτ" underline>PPS</info-tip></th>
                                <th>Πόντοι</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in analytics.profile[side.key].rows" :key="row.key">
                                <td class="aa-table__first"><i class="aa-dot" :class="`aa-dot--${row.key}`" />{{ row.label }}</td>
                                <td>{{ row.made }}/{{ row.attempts }}</td>
                                <td :class="levelClass(row.fgPct, profileLimits[row.key])">{{ fmt(row.fgPct, 0, '%') }}</td>
                                <td class="aa-strong">{{ fmt(row.pps, 2) }}</td>
                                <td>{{ row.points }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <!-- ---------- Advanced shot details from the stats app ---------- -->
        <template v-if="analytics.details">
            <div class="aa-section-title">
                <span class="aa-section-title__bar custom-orange" />
                Ανάλυση επιθέσεων
            </div>
            <div class="aa-card">
                <div class="aa-card__head aa-card__head--wrap">
                    <div class="aa-toggle">
                        <button
                            v-for="side in teams"
                            :key="`details-${side.key}`"
                            type="button"
                            class="aa-toggle__btn"
                            :class="{ 'aa-toggle__btn--active': detailsTeam === side.key }"
                            @click="detailsTeam = side.key"
                        >
                            <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                            <span>{{ side.team.name }}</span>
                        </button>
                    </div>
                    <div class="aa-groups">
                        <button
                            v-for="group in detailsData.groups"
                            :key="group.key"
                            type="button"
                            class="aa-groups__btn"
                            :class="{ 'aa-groups__btn--active': detailsGroup === group.key }"
                            @click="detailsGroup = group.key"
                        >
                            {{ group.title }}
                            <small>{{ group.subtitle }}</small>
                        </button>
                    </div>
                </div>
                <div class="aa-coverage">
                    Στοιχεία σε {{ detailsData.tagged }} από {{ detailsData.shots }} σουτ ({{ fmt(percentOf(detailsData.tagged, detailsData.shots), 0) }}%)
                    <template v-if="currentGroup"> · {{ currentGroup.tagged }} με «{{ currentGroup.title }}»</template>
                </div>
                <div v-if="currentGroup && currentGroup.rows.length" class="aa-table-wrap">
                    <table class="aa-table aa-table--wide">
                        <thead>
                            <tr>
                                <th class="aa-table__first">{{ currentGroup.title }}</th>
                                <th>Συχνότητα</th>
                                <th><info-tip text="Εύστοχα / προσπάθειες" underline>Σουτ</info-tip></th>
                                <th>FG%</th>
                                <th><info-tip text="Effective FG%: το τρίποντο μετράει 1,5 φορά" underline>eFG%</info-tip></th>
                                <th>Πόντοι</th>
                                <th><info-tip text="Πόντοι ανά σουτ" underline>PPS</info-tip></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in currentGroup.rows" :key="row.value">
                                <td class="aa-table__first">
                                    <info-tip :text="row.definition" :underline="!!row.definition"><span class="aa-strong">{{ row.label }}</span></info-tip>
                                    <small v-if="row.hint" class="aa-hint">{{ row.hint }}</small>
                                </td>
                                <td>
                                    <span class="aa-freq">
                                        <span class="aa-freq__bar"><span :style="{ width: `${row.share}%`, background: detailsColor }" /></span>
                                        {{ fmt(row.share, 0) }}%
                                    </span>
                                </td>
                                <td>{{ row.made }}/{{ row.attempts }}</td>
                                <td>{{ fmt(row.fgPct, 0, '%') }}</td>
                                <td>{{ fmt(row.efg, 0, '%') }}</td>
                                <td>{{ row.points }}</td>
                                <td class="aa-strong" :class="levelClass(row.pps, [1.1, 0.9])">{{ fmt(row.pps, 2) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="aa-empty">Δεν υπάρχουν σουτ με αυτό το στοιχείο.</div>
            </div>
        </template>

        <!-- ---------- Players ---------- -->
        <div class="aa-section-title">
            <span class="aa-section-title__bar custom-orange" />
            Παίκτες · προχωρημένα
        </div>
        <div class="aa-card">
            <div class="aa-card__head">
                <div class="aa-toggle">
                    <button
                        v-for="side in teams"
                        :key="`players-${side.key}`"
                        type="button"
                        class="aa-toggle__btn"
                        :class="{ 'aa-toggle__btn--active': playersTeam === side.key }"
                        @click="playersTeam = side.key"
                    >
                        <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                        <span>{{ side.team.name }}</span>
                    </button>
                </div>
            </div>
            <div class="aa-table-wrap">
                <table class="aa-table aa-table--wide">
                    <thead>
                        <tr>
                            <th class="aa-table__first">Παίκτης</th>
                            <th v-for="column in playerColumns" :key="column.key">
                                <info-tip :text="column.info" :underline="!!column.info">{{ column.label }}</info-tip>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in analytics.players[playersTeam]" :key="row.id" class="aa-player-row" @click="goToPlayer(row.id)">
                            <td class="aa-table__first">
                                <span class="aa-player">
                                    <span class="aa-player__jersey">#{{ row.jersey }}</span>
                                    {{ row.name }}
                                </span>
                            </td>
                            <td>{{ minutesLabel(row.minutes) }}</td>
                            <td class="aa-strong">{{ row.pts }}</td>
                            <td v-if="analytics.plusMinusTracked" :class="plusMinusClass(row.plusMinus)">{{ signed(row.plusMinus) }}</td>
                            <td>{{ fmt(row.ts, 0, '%') }}</td>
                            <td>{{ fmt(row.efg, 0, '%') }}</td>
                            <td>{{ fmt(row.usage, 0, '%') }}</td>
                            <td>{{ fmt(row.astPct, 0, '%') }}</td>
                            <td>{{ row.astTo === null ? (row.ast ? `${row.ast}/0` : '–') : fmt(row.astTo, 1) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="aa-legend">
                <span v-for="column in playerColumns.filter(item => item.info)" :key="`legend-${column.key}`"><b>{{ column.label }}</b> {{ column.info }}</span>
            </div>
        </div>

        <!-- ---------- Assist network + lineups ---------- -->
        <template v-if="hasAssists">
            <div class="aa-section-title">
                <span class="aa-section-title__bar custom-orange" />
                Δίκτυο ασίστ
            </div>
            <div class="aa-two-cols">
                <div v-for="side in teams" :key="`assists-${side.key}`" class="aa-card">
                    <div class="aa-card__head">
                        <span class="aa-team">
                            <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                            <span>{{ side.team.name }}</span>
                        </span>
                        <span class="aa-card__meta">ποιος έδωσε σε ποιον</span>
                    </div>
                    <div v-for="pair in analytics.assists[side.key]" :key="`${pair.from}-${pair.to}`" class="aa-pair">
                        <span class="aa-pair__names">
                            <b>{{ pair.fromName }}</b>
                            <v-icon small :color="side.key === 'team1' ? color1 : color2">mdi-arrow-right-thin</v-icon>
                            {{ pair.toName }}
                        </span>
                        <span class="aa-pair__count">{{ pair.count }} ασ. · {{ pair.points }} π.</span>
                    </div>
                    <div v-if="!analytics.assists[side.key].length" class="aa-empty">Δεν καταγράφηκαν ασίστ.</div>
                </div>
            </div>
        </template>

        <template v-if="analytics.lineups">
            <div class="aa-section-title">
                <span class="aa-section-title__bar custom-orange" />
                Πεντάδες
            </div>
            <div class="aa-two-cols">
                <div v-for="side in teams" :key="`lineups-${side.key}`" class="aa-card">
                    <div class="aa-card__head">
                        <span class="aa-team">
                            <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                            <span>{{ side.team.name }}</span>
                        </span>
                        <span class="aa-card__meta">με τα περισσότερα λεπτά</span>
                    </div>
                    <div v-if="analytics.lineups[side.key].length" class="aa-lineup aa-lineup--head">
                        <div class="aa-lineup__players">Πεντάδα</div>
                        <div class="aa-lineup__numbers">
                            <span><info-tip text="Λεπτά που έπαιξαν μαζί" underline>Λεπτά</info-tip></span>
                            <span><info-tip text="Πόντοι που έβαλε − πόντοι που δέχτηκε η ομάδα όσο ήταν μαζί στο παρκέ" underline>Σκορ</info-tip></span>
                            <span><info-tip text="Διαφορά σκορ όσο ήταν μαζί στο παρκέ" underline>+/-</info-tip></span>
                        </div>
                    </div>
                    <div v-for="lineup in analytics.lineups[side.key]" :key="lineup.key" class="aa-lineup">
                        <div class="aa-lineup__players">{{ lineup.players.join(' · ') }}</div>
                        <div class="aa-lineup__numbers">
                            <span>{{ minutesLabel(lineup.seconds / 60) }}</span>
                            <span>{{ lineup.pf }}-{{ lineup.pa }}</span>
                            <span class="aa-strong" :class="plusMinusClass(lineup.plusMinus)">{{ signed(lineup.plusMinus) }}</span>
                        </div>
                    </div>
                    <div v-if="!analytics.lineups[side.key].length" class="aa-empty">Καμία πεντάδα δεν έπαιξε πάνω από 2 λεπτά.</div>
                </div>
            </div>
        </template>

        <!-- ---------- Clutch ---------- -->
        <template v-if="analytics.clutch">
            <div class="aa-section-title">
                <span class="aa-section-title__bar custom-orange" />
                Clutch
                <span class="aa-chip">τελευταία 5′ με διαφορά έως 5</span>
            </div>
            <div class="aa-card aa-card--narrow">
                <div class="aa-teams">
                    <span v-for="side in teams" :key="`clutch-team-${side.key}`" class="aa-team" :class="{ 'aa-team--right': side.key === 'team2' }">
                        <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                        <span>{{ side.team.name }}</span>
                    </span>
                </div>
                <div v-for="row in clutchRows" :key="row.key" class="aa-row">
                    <div class="aa-value" :class="{ 'aa-value--lead': row.lead === 1 }">{{ row.display1 }}</div>
                    <div class="aa-middle">
                        <span class="aa-label">{{ row.label }}</span>
                        <div class="aa-bars">
                            <div class="aa-bar aa-bar--left"><span :style="{ width: `${row.share1}%`, background: color1 }" /></div>
                            <div class="aa-bar"><span :style="{ width: `${row.share2}%`, background: color2 }" /></div>
                        </div>
                    </div>
                    <div class="aa-value aa-value--right" :class="{ 'aa-value--lead': row.lead === 2 }">{{ row.display2 }}</div>
                </div>
                <div class="aa-clutch-top">
                    <span>{{ clutchTop(analytics.clutch.team1) }}</span>
                    <span class="aa-clutch-top__label">Πρώτος σκόρερ</span>
                    <span class="aa-clutch-top__right">{{ clutchTop(analytics.clutch.team2) }}</span>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import { buildAdvancedAnalytics } from '@/utils/advancedAnalytics'
    import { getTeamColors } from '@/utils/shotChart'

    // [good, average] FG% of the zones of the shot profile
    const PROFILE_LIMITS = { paint: [55, 45], mid: [42, 34], corner3: [38, 30], above3: [35, 28] }

    // The advanced analytics tab of a played match: possession based efficiency, the four factors, shot profile,
    // the advanced shot details of the stats app, players' advanced numbers, assists, lineups and clutch
    export default {
        name: 'AdvancedAnalytics',
        props: {
            match: {
                type: Object,
                default: null
            },
            logs: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                detailsTeam: 'team1',
                detailsGroup: 'playType',
                playersTeam: 'team1',
                profileLimits: PROFILE_LIMITS
            }
        },
        computed: {
            analytics() {
                return buildAdvancedAnalytics(this.match, this.logs)
            },
            teams() {
                return ['team1', 'team2'].map(key => ({ key, team: (this.match && this.match[key]) || {} }))
            },
            teamColors() {
                return getTeamColors(this.match && this.match.team1, this.match && this.match.team2)
            },
            color1() {
                return this.teamColors.team1
            },
            color2() {
                return this.teamColors.team2
            },
            detailsColor() {
                return this.detailsTeam === 'team1' ? this.color1 : this.color2
            },
            efficiencyRows() {
                const e = this.analytics.efficiency
                const a = e.team1
                const b = e.team2
                return [
                    { key: 'ortg', label: 'Επιθετική αποδοτικότητα', info: 'Offensive rating: πόντοι ανά 100 κατοχές', v1: a.ortg, v2: b.ortg, digits: 1 },
                    { key: 'drtg', label: 'Αμυντική αποδοτικότητα', info: 'Defensive rating: πόντοι που δέχτηκε ανά 100 κατοχές (λιγότεροι = καλύτερα)', v1: a.drtg, v2: b.drtg, digits: 1, lower: true },
                    { key: 'net', label: 'Net rating', info: 'Διαφορά πόντων ανά 100 κατοχές', v1: a.net, v2: b.net, digits: 1, signed: true },
                    { key: 'ts', label: 'TS%', info: 'True shooting: πόντοι / (2 × (σουτ + 0,44 × βολές)), μετράει μαζί δίποντα, τρίποντα και βολές', v1: a.ts, v2: b.ts, suffix: '%' },
                    { key: 'efg', label: 'eFG%', info: 'Effective FG%: (εύστοχα + 0,5 × εύστοχα τρίποντα) / σουτ. Four factor #1', v1: a.efg, v2: b.efg, suffix: '%' },
                    { key: 'tov', label: 'TOV%', info: 'Λάθη ανά 100 κατοχές (λιγότερα = καλύτερα). Four factor #2', v1: a.tov, v2: b.tov, suffix: '%', lower: true },
                    { key: 'orb', label: 'ORB%', info: 'Ποσοστό των διαθέσιμων επιθετικών ριμπάουντ που πήρε. Four factor #3', v1: a.orb, v2: b.orb, suffix: '%' },
                    { key: 'ftr', label: 'FT rate', info: 'Εύστοχες βολές ανά σουτ εντός παιδιάς. Four factor #4', v1: a.ftr, v2: b.ftr, digits: 2 },
                    { key: 'drb', label: 'DRB%', info: 'Ποσοστό των διαθέσιμων αμυντικών ριμπάουντ που πήρε', v1: a.drb, v2: b.drb, suffix: '%' },
                    { key: 'ast', label: 'AST%', info: 'Ποσοστό των εύστοχων σουτ που ήρθαν από ασίστ', v1: a.astPct, v2: b.astPct, suffix: '%' },
                    { key: 'astTo', label: 'AST/TO', info: 'Ασίστ ανά λάθος', v1: a.astTo, v2: b.astTo, digits: 2 },
                    { key: 'pps', label: 'Πόντοι ανά σουτ', info: 'Πόντοι από δίποντα και τρίποντα ανά σουτ εντός παιδιάς', v1: a.pps, v2: b.pps, digits: 2 }
                ].map(row => this.compareRow(row))
            },
            sourceRows() {
                const s1 = this.analytics.sources.team1
                const s2 = this.analytics.sources.team2
                const rows = [
                    { key: 'paint', label: 'Ρακέτα', info: 'Πόντοι από σουτ μέσα στη ρακέτα', v1: s1.paint, v2: s2.paint },
                    { key: 'assist', label: 'Από ασίστ', info: 'Πόντοι από καλάθια με ασίστ', v1: s1.assist, v2: s2.assist },
                    { key: 'secondChance', label: '2ης ευκαιρίας', info: 'Πόντοι στην ίδια κατοχή μετά από επιθετικό ριμπάουντ', v1: s1.secondChance, v2: s2.secondChance },
                    { key: 'offTurnovers', label: 'Από λάθη αντιπάλου', info: 'Πόντοι στην κατοχή μετά από λάθος του αντιπάλου', v1: s1.offTurnovers, v2: s2.offTurnovers }
                ]
                if (s1.fastbreak !== null || s2.fastbreak !== null) {
                    rows.push({ key: 'fastbreak', label: 'Αιφνιδιασμοί', info: 'Πόντοι από σουτ με «Fastbreak» στα στοιχεία επίθεσης (μόνο όσα σουτ έχουν στοιχεία)', v1: s1.fastbreak || 0, v2: s2.fastbreak || 0 })
                }
                if (s1.bench !== null && s2.bench !== null) {
                    rows.push({ key: 'bench', label: 'Πάγκος', info: 'Πόντοι των παικτών που δεν ξεκίνησαν', v1: s1.bench, v2: s2.bench })
                }
                rows.push({ key: 'freeThrows', label: 'Βολές', info: 'Πόντοι από βολές', v1: s1.freeThrows, v2: s2.freeThrows })
                return rows.map(row => ({
                    ...this.compareRow({ ...row, digits: 0 }),
                    detail1: ` ${this.fmt(this.percentOf(row.v1, s1.total), 0)}%`,
                    detail2: ` ${this.fmt(this.percentOf(row.v2, s2.total), 0)}%`
                }))
            },
            clutchRows() {
                const a = this.analytics.clutch.team1
                const b = this.analytics.clutch.team2
                const rows = [
                    { key: 'pts', label: 'Πόντοι', v1: a.pts, v2: b.pts, digits: 0 },
                    { key: 'fg', label: 'Σουτ', v1: a.fgm, v2: b.fgm, display1: `${a.fgm}/${a.fga}`, display2: `${b.fgm}/${b.fga}` },
                    { key: 'ft', label: 'Βολές', v1: a.ftm, v2: b.ftm, display1: `${a.ftm}/${a.fta}`, display2: `${b.ftm}/${b.fta}` },
                    { key: 'tov', label: 'Λάθη', v1: a.tov, v2: b.tov, digits: 0, lower: true },
                    { key: 'reb', label: 'Ριμπάουντ', v1: a.orb + a.drb, v2: b.orb + b.drb, digits: 0 }
                ].map(row => this.compareRow(row))
                return rows
            },
            detailsData() {
                return this.analytics.details ? this.analytics.details[this.detailsTeam] : null
            },
            currentGroup() {
                return this.detailsData ? this.detailsData.groups.find(group => group.key === this.detailsGroup) : null
            },
            playerColumns() {
                return [
                    { key: 'min', label: 'Λεπ' },
                    { key: 'pts', label: 'Πόν' },
                    this.analytics.plusMinusTracked ? { key: 'pm', label: '+/-', info: 'Διαφορά σκορ όσο ήταν στο παρκέ' } : null,
                    { key: 'ts', label: 'TS%', info: 'Πραγματικό ποσοστό ευστοχίας (με βολές)' },
                    { key: 'efg', label: 'eFG%', info: 'Ευστοχία με το τρίποντο να μετράει 1,5 φορά' },
                    { key: 'usg', label: 'USG%', info: 'Ποσοστό των κατοχών της ομάδας που «τελείωσε» όσο ήταν στο παρκέ' },
                    { key: 'astPct', label: 'AST%', info: 'Ποσοστό των καλαθιών των συμπαικτών του με δική του ασίστ, όσο ήταν στο παρκέ' },
                    { key: 'astTo', label: 'AST/TO', info: 'Ασίστ ανά λάθος' }
                ].filter(Boolean)
            },
            hasAssists() {
                return !!this.analytics.assists && (this.analytics.assists.team1.length + this.analytics.assists.team2.length) > 0
            }
        },
        methods: {
            fmt(value, digits = 1, suffix = '') {
                if (value === null || value === undefined || !Number.isFinite(Number(value))) return '–'
                return `${Number(value).toFixed(digits)}${suffix}`
            },
            percentOf(part, total) {
                return total ? (part / total) * 100 : null
            },
            signed(value) {
                if (value === null || value === undefined) return '–'
                return value > 0 ? `+${value}` : String(value)
            },
            plusMinusClass(value) {
                if (!value) return ''
                return value > 0 ? 'aa-positive' : 'aa-negative'
            },
            // hot / ok / cold against [good, average] limits
            levelClass(value, limits) {
                if (value === null || value === undefined || !limits) return ''
                if (value >= limits[0]) return 'aa-level--hot'
                if (value >= limits[1]) return 'aa-level--ok'
                return 'aa-level--cold'
            },
            clutchTop(side) {
                return side.topScorer ? `${side.topScorer.name} · ${side.topScorer.points} π.` : '–'
            },
            minutesLabel(minutes) {
                const total = Math.round((Number(minutes) || 0) * 60)
                return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
            },
            // a row of the team comparison: displays, the share of each bar and who is better
            compareRow(row) {
                const v1 = row.v1 === null || row.v1 === undefined ? null : Number(row.v1)
                const v2 = row.v2 === null || row.v2 === undefined ? null : Number(row.v2)
                const display = value => {
                    if (value === null) return '–'
                    if (row.signed) return value > 0 ? `+${value.toFixed(row.digits || 0)}` : value.toFixed(row.digits || 0)
                    return this.fmt(value, row.digits === undefined ? 0 : row.digits, row.suffix || '')
                }
                // ratings can be negative (net): the bars compare the distance from the lower value
                const base = Math.min(0, v1 || 0, v2 || 0)
                const total = ((v1 || 0) - base) + ((v2 || 0) - base)
                let lead = 0
                if (v1 !== null && v2 !== null && v1 !== v2) {
                    lead = (row.lower ? v1 < v2 : v1 > v2) ? 1 : 2
                }
                return {
                    ...row,
                    display1: row.display1 || display(v1),
                    display2: row.display2 || display(v2),
                    share1: total ? (((v1 || 0) - base) / total) * 100 : 0,
                    share2: total ? (((v2 || 0) - base) / total) * 100 : 0,
                    lead
                }
            },
            goToPlayer(id) {
                if (id) this.$router.push({ name: 'Player', params: { id } })
            }
        }
    }
</script>

<style scoped>
.aa {
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

/* ---------- Same section titles and cards as the rest of the match page ---------- */
.aa-section-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin: 22px 0 12px;
    padding: 8px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 1.1rem;
    font-weight: 800;
}

.aa-section-title:first-child {
    margin-top: 0;
}

.aa-section-title__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.aa-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 999px;
    background: #fff3e0;
    color: #8a4b00;
    font-size: 0.75rem;
    font-weight: 700;
}

.aa-two-cols {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    align-items: start;
}

.aa-card {
    padding: 16px 18px 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.aa-card--narrow {
    max-width: 720px;
    margin: 0 auto;
}

.aa-card__subtitle {
    margin-bottom: 12px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.aa-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.aa-card__head--wrap {
    flex-wrap: wrap;
}

.aa-card__meta {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.75rem;
    font-weight: 700;
}

.aa-note,
.aa-coverage,
.aa-empty {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
}

.aa-note {
    margin-top: 8px;
}

.aa-coverage {
    margin-bottom: 10px;
}

.aa-empty {
    padding: 10px 0;
}

/* ---------- Teams ---------- */
.aa-teams {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.aa-team {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.15;
}

.aa-team--right {
    flex-direction: row-reverse;
    text-align: right;
}

.aa-team img {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    padding: 2px;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
    object-fit: contain;
}

/* ---------- Comparison rows (as the team comparison) ---------- */
.aa-row {
    display: grid;
    grid-template-columns: 78px 1fr 78px;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.aa-row:last-of-type {
    border-bottom: none;
}

.aa-value {
    display: flex;
    flex-direction: column;
    color: rgba(0, 0, 0, 0.6);
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
}

.aa-value small {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.7rem;
    font-weight: 600;
}

.aa-value--right {
    align-items: flex-end;
    text-align: right;
}

.aa-value--lead {
    color: #111;
    font-size: 1.12rem;
    font-weight: 800;
}

.aa-middle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 0;
}

.aa-label {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.aa-info {
    color: rgba(0, 0, 0, 0.35) !important;
    cursor: help;
}

.aa-bars {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    width: 100%;
}

.aa-bar {
    display: flex;
    height: 8px;
    border-radius: 4px;
    background: #f1f1f3;
    overflow: hidden;
}

.aa-bar--left {
    justify-content: flex-end;
}

.aa-bar span {
    display: block;
    height: 100%;
    border-radius: 4px;
    transition: width 0.4s ease;
}

/* ---------- Shot diet ---------- */
.aa-diet {
    display: flex;
    gap: 2px;
    height: 22px;
    margin-bottom: 12px;
    border-radius: 6px;
    overflow: hidden;
}

.aa-diet__part {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
}

.aa-diet__part--paint,
.aa-dot--paint {
    background: #ef4444;
}

.aa-diet__part--mid,
.aa-dot--mid {
    background: #ff9800;
}

.aa-diet__part--corner3,
.aa-dot--corner3 {
    background: #2b2b2e;
}

.aa-diet__part--above3,
.aa-dot--above3 {
    background: #6b6b75;
}

.aa-dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    margin-right: 6px;
    border-radius: 50%;
}

/* ---------- Tables (as the box score) ---------- */
.aa-table-wrap {
    overflow: auto;
    border: 1px solid rgba(0, 0, 0, 0.07);
    border-radius: 10px;
}

.aa-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    color: #2b2b2e;
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;
}

.aa-table th,
.aa-table td {
    padding: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    text-align: center;
    white-space: nowrap;
}

.aa-table th {
    background: #0d0d0f;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
}

.aa-table th:first-child {
    border-top-left-radius: 8px;
}

.aa-table th:last-child {
    border-top-right-radius: 8px;
}

.aa-table--wide th:first-child,
.aa-table--wide th:last-child {
    border-radius: 0;
}

.aa-table__first {
    text-align: left !important;
}

.aa-table--wide .aa-table__first {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    box-shadow: 1px 0 0 rgba(0, 0, 0, 0.06);
}

.aa-table--wide th.aa-table__first {
    z-index: 2;
    background: #0d0d0f;
}

.aa-strong {
    font-weight: 800;
}

.aa-hint {
    display: block;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.7rem;
}

.aa-freq {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.aa-freq__bar {
    display: inline-block;
    width: 60px;
    height: 6px;
    border-radius: 3px;
    background: #f1f1f3;
    overflow: hidden;
}

.aa-freq__bar span {
    display: block;
    height: 100%;
}

.aa-level--hot {
    color: #2e7d32;
    font-weight: 800;
}

.aa-level--ok {
    color: #ef6c00;
    font-weight: 700;
}

.aa-level--cold {
    color: #c62828;
    font-weight: 700;
}

.aa-positive {
    color: #2e7d32;
    font-weight: 800;
}

.aa-negative {
    color: #c62828;
    font-weight: 800;
}

.aa-player-row {
    cursor: pointer;
}

.aa-player-row:hover td {
    background: #fff8f2;
}

.aa-player {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
}

.aa-player__jersey {
    min-width: 30px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.78rem;
}

.aa-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 10px;
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.72rem;
}

/* ---------- Team toggle (as the box score) and group buttons ---------- */
.aa-toggle {
    display: inline-flex;
    gap: 2px;
    max-width: 100%;
    padding: 3px;
    border-radius: 999px;
    background: #0d0d0f;
}

.aa-toggle__btn {
    display: inline-flex;
    flex: 0 1 auto;
    align-items: center;
    gap: 6px;
    min-width: 0;
    padding: 5px 16px 5px 6px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.88rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease;
}

.aa-toggle__btn span {
    overflow: hidden;
    text-overflow: ellipsis;
}

.aa-toggle__btn--active {
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
}

.aa-toggle__btn img {
    width: 26px;
    height: 26px;
    padding: 1px;
    border-radius: 50%;
    background: #fff;
    object-fit: contain;
}

.aa-groups {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.aa-groups__btn {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 5px 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    background: #fff;
    font-size: 0.82rem;
    font-weight: 800;
    line-height: 1.15;
}

.aa-groups__btn small {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.68rem;
    font-weight: 600;
}

.aa-groups__btn--active {
    border-color: #ff7133;
    background: #fff3ec;
    color: #c2410c;
}

/* ---------- Assist network, lineups ---------- */
.aa-pair,
.aa-lineup {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    font-size: 0.88rem;
}

.aa-pair:last-of-type,
.aa-lineup:last-of-type {
    border-bottom: none;
}

.aa-pair__names {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    min-width: 0;
}

.aa-pair__count {
    flex: 0 0 auto;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.aa-lineup__players {
    min-width: 0;
    font-weight: 700;
    line-height: 1.3;
}

.aa-lineup__numbers {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 6px;
    color: rgba(0, 0, 0, 0.6);
    font-variant-numeric: tabular-nums;
}

/* fixed widths: the numbers line up under the column titles */
.aa-lineup__numbers > span {
    text-align: right;
}

.aa-lineup__numbers > span:nth-child(1) {
    width: 46px;
}

.aa-lineup__numbers > span:nth-child(2) {
    width: 52px;
}

.aa-lineup__numbers > span:nth-child(3) {
    width: 34px;
}

.aa-lineup--head {
    padding: 6px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.aa-lineup--head .aa-lineup__players,
.aa-lineup--head .aa-lineup__numbers {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.aa-lineup--head .aa-lineup__numbers > span {
    cursor: help;
}

.aa-clutch-top {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 0.88rem;
    font-weight: 800;
}

.aa-clutch-top__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
}

.aa-clutch-top__right {
    text-align: right;
}

@media (max-width: 960px) {
    .aa-two-cols {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 600px) {
    .aa-card {
        padding: 14px 12px 10px;
    }

    .aa-row {
        grid-template-columns: 64px 1fr 64px;
        gap: 8px;
    }

    .aa-team {
        font-size: 0.92rem;
    }

    .aa-lineup {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .aa-lineup__numbers {
        align-self: flex-end;
    }

    .aa-lineup--head .aa-lineup__players {
        display: none;
    }
}
</style>
